/**
 * Vulnerability Detail Sheet Component
 *
 * Displays full details of a single finding with rich remediation guidance.
 * Similar to ComplianceOverview's detail sheet with actionable sections.
 */

import React from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, Shield, Code, Loader2, FileText } from "lucide-react";
import type { LLMScanResult, RemediationGuidance } from "@/types/llm";
import { getSeverityColor } from "@/utils/llmFormatters";

interface VulnerabilityDetailModalProps {
  result: LLMScanResult | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formatPluginName = (pluginId: string): string => {
  const parts = pluginId.split(":");
  if (parts.length > 1) {
    return parts[1]
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }
  return pluginId;
};

const parseRemediationGuidance = (jsonStr: string | null): RemediationGuidance | null => {
  if (!jsonStr) return null;
  try {
    return JSON.parse(jsonStr) as RemediationGuidance;
  } catch {
    return null;
  }
};

export const VulnerabilityDetailModal: React.FC<VulnerabilityDetailModalProps> = ({
  result,
  open,
  onOpenChange,
}) => {
  if (!result) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-[600px] sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Vulnerability Details</SheetTitle>
          </SheetHeader>
        </SheetContent>
      </Sheet>
    );
  }

  const remediation = parseRemediationGuidance(result.remediation_guidance);
  const isGenerating = !remediation && result.remediation_generated_at === null;
  const isFailed = result.status === "FAIL";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[600px] sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex-1">
              <SheetTitle className="text-base font-bold">
                {formatPluginName(result.plugin_id)}
              </SheetTitle>
              <div className="text-sm text-muted-foreground mt-1">{result.plugin_id}</div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4 mt-2">
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-1">Status</div>
              <Badge
                variant={result.status === "PASS" ? "default" : "destructive"}
                className="text-xs"
              >
                {result.status}
              </Badge>
            </div>
            {result.severity && (
              <div>
                <div className="text-xs font-medium text-muted-foreground mb-1">Severity</div>
                <Badge className={`${getSeverityColor(result.severity)} text-xs`}>
                  {result.severity.toUpperCase()}
                </Badge>
              </div>
            )}
          </div>
          {result.goal && (
            <SheetDescription className="text-sm mt-2">{result.goal}</SheetDescription>
          )}
        </SheetHeader>

        <div className="space-y-4 mt-4">
          {/* Risk Assessment - Only for failed tests */}
          {isFailed && remediation && (
            <Card className="border-orange-200 bg-orange-50/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-orange-600" />
                  Risk Assessment
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">{remediation.risk_explanation}</p>
              </CardContent>
            </Card>
          )}

          {/* Remediation Guidance - Only for failed tests */}
          {isFailed && (
            <Card className="border-blue-200 bg-blue-50/50">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Shield className="h-4 w-4 text-blue-600" />
                  Remediation Guidance
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {isGenerating && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Generating remediation guidance...</span>
                  </div>
                )}
                {!isGenerating && !remediation && (
                  <div className="text-sm text-muted-foreground">
                    Remediation guidance is not yet available. Please check back later.
                  </div>
                )}
                {remediation && (
                  <>
                    <div>
                      <div className="text-xs font-medium text-muted-foreground mb-2">
                        Estimated Effort
                      </div>
                      <Badge
                        className={
                          remediation.estimated_effort === "Low"
                            ? "bg-green-100 text-green-800"
                            : remediation.estimated_effort === "Medium"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                        }
                      >
                        {remediation.estimated_effort}
                      </Badge>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-muted-foreground mb-2">
                        Remediation Steps
                      </div>
                      <ul className="list-inside space-y-2 text-sm">
                        {remediation.remediation_steps.map((step, index) => (
                          <li key={index} className="pl-2">
                            {step}
                          </li>
                        ))}
                      </ul>
                    </div>
                    {remediation.code_example && (
                      <div>
                        <div className="text-xs font-medium text-muted-foreground mb-2">
                          Code Example
                        </div>
                        <pre className="text-xs bg-muted p-3 rounded-md overflow-x-auto">
                          <code>{remediation.code_example}</code>
                        </pre>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* Compliance Impact - Only for failed tests with remediation */}
          {isFailed && remediation && remediation.compliance_impact.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <FileText className="h-4 w-4 text-purple-600" />
                  Compliance Impact
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {remediation.compliance_impact.map((framework, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {framework}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Technical Details */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Code className="h-4 w-4 text-gray-600" />
                Technical Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Plugin ID:</span>
                <span className="font-mono text-xs break-all">{result.plugin_id}</span>
              </div>
              {result.goal && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-1">Test Goal</div>
                  <div className="text-sm">{result.goal}</div>
                </div>
              )}
              {result.reason && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-1">
                    {result.status === "FAIL" ? "Failure Reason" : "Test Result"}
                  </div>
                  <div className="text-sm whitespace-pre-wrap bg-muted p-2 rounded-md">
                    {result.reason}
                  </div>
                </div>
              )}
              {result.test_idx !== null && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Test Index:</span>
                  <span className="font-medium">{result.test_idx}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </SheetContent>
    </Sheet>
  );
};
